import { Controller } from "@hotwired/stimulus"
import {
  isPushSupported,
  getPermissionState,
  getPushSupportDetails,
  isCurrentlySubscribed,
  subscribeToPush,
  unsubscribeFromPush
} from "../common/push-notifications"

// Push subscription controller
//
// Drives the "Enable Notifications" button on the user settings page. It asks
// the browser for notification permission, obtains a PushSubscription from the
// service worker registration, and POSTs it to the backend
// (PushSubscriptionsController#create at /push_subscriptions). The heavy
// lifting (VAPID key handling, service-worker registration, subscribe, POST)
// lives in common/push-notifications so this controller only owns the UI.
//
// Usage:
//   <div data-controller="push-subscription">
//     <p data-push-subscription-target="status"></p>
//     <button type="button"
//             data-push-subscription-target="button"
//             data-action="push-subscription#toggle">Enable Notifications</button>
//   </div>
//
export default class extends Controller {
  static targets = ["button", "status"]

  connect() {
    this.refresh()
  }

  // Reflect the current support/permission/subscription state in the UI.
  async refresh() {
    if (!isPushSupported()) {
      const details = getPushSupportDetails()
      this.setStatus(details.reason || "Push notifications aren't supported in this browser.", "error")
      this.disableButton("Notifications unavailable")
      return
    }

    if (getPermissionState() === "denied") {
      this.setStatus(
        "Notifications are blocked. Enable them for this site in your browser settings, then reload.",
        "error"
      )
      this.disableButton("Notifications blocked")
      return
    }

    const subscribed = await isCurrentlySubscribed()
    if (subscribed) {
      this.setStatus("Notifications are active on this browser.", "success")
      this.setButton("Disable notifications", "disable")
    } else {
      this.setStatus("Get spoken updates from the agent even when this tab is in the background.", "muted")
      this.setButton("Enable Notifications", "enable")
    }
  }

  // Single entry point wired to the button; routes to enable/disable based on
  // the current mode the button is in.
  toggle(event) {
    event?.preventDefault()
    if (this.mode === "disable") {
      this.disable()
    } else {
      this.enable()
    }
  }

  // Ask permission, subscribe, and POST the PushSubscription to the backend.
  async enable() {
    if (!this.hasButtonTarget) return

    this.buttonTarget.disabled = true
    this.buttonTarget.textContent = "Enabling…"
    this.setStatus("Waiting for notification permission…", "muted")

    const result = await subscribeToPush()

    if (result.success) {
      this.setStatus("Notifications are active on this browser.", "success")
      this.setButton("Disable notifications", "disable")
    } else {
      this.setStatus(result.error || "Couldn't enable notifications. Please try again.", "error")
      this.setButton("Enable Notifications", "enable")
    }
  }

  // Remove the subscription locally and on the server.
  async disable() {
    if (!this.hasButtonTarget) return

    this.buttonTarget.disabled = true
    this.buttonTarget.textContent = "Disabling…"

    await unsubscribeFromPush()
    await this.refresh()
  }

  // --- UI helpers -------------------------------------------------------

  setButton(label, mode) {
    this.mode = mode
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = false
    this.buttonTarget.textContent = label
    this.buttonTarget.classList.toggle("btn-primary", mode === "enable")
    this.buttonTarget.classList.toggle("btn-error", mode === "disable")
    this.buttonTarget.classList.toggle("btn-outline", mode === "disable")
  }

  disableButton(label) {
    this.mode = null
    if (!this.hasButtonTarget) return
    this.buttonTarget.disabled = true
    this.buttonTarget.textContent = label
  }

  setStatus(message, tone = "muted") {
    if (!this.hasStatusTarget) return
    const tones = {
      muted: "text-base-content/60",
      success: "text-success",
      error: "text-error"
    }
    this.statusTarget.className = `label-text-alt ${tones[tone] || tones.muted}`
    this.statusTarget.textContent = message
  }
}
